import { useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Resets the window scroll offset on navigation.
 *
 * React Router preserves the scroll position across route changes, so following
 * a footer link from the bottom of a long page (Privacy, Terms, Pricing) used to
 * land the user at the bottom of the new page. Every navigation now lands at the
 * top — EXCEPT hash links like `/#how-it-works`, which deliberately target a
 * section and scroll to that element instead.
 *
 * Two non-obvious details, both measured rather than assumed:
 *
 * 1. `behavior: 'instant'` is required, not cosmetic. index.css sets a global
 *    `scroll-behavior: smooth`, so the default would animate the whole page
 *    upward on every route change.
 * 2. The unconditional reset-to-top must happen BEFORE the anchor scroll. When
 *    the new page is shorter than the old one, the browser re-clamps the
 *    carried-over offset to the new document height, and that clamp lands after
 *    this effect — it silently undid anchor scrolls issued here (the section
 *    ended up at the old page's max scroll instead). Resetting to 0 first
 *    removes the hazard, because 0 can never overflow the new height.
 *
 * A same-page anchor click keeps the nicer smooth animation; one arrived at from
 * another page jumps instantly, which is also what a browser does with a hash on
 * a fresh page load. `block: 'start'` preserves each target's `scroll-mt-*`
 * offset so the fixed nav doesn't cover the heading.
 *
 * Rendered once inside <BrowserRouter> in App.jsx; renders nothing.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation()
  const prevPathname = useRef(pathname)

  useLayoutEffect(() => {
    const samePage = prevPathname.current === pathname
    prevPathname.current = pathname

    if (!samePage) window.scrollTo({ top: 0, left: 0, behavior: 'instant' })

    if (hash) {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)))
      // A missing target (stale/bad anchor) just leaves us at the top.
      if (el) el.scrollIntoView({ behavior: samePage ? 'smooth' : 'instant', block: 'start' })
    } else if (samePage) {
      // Same path, hash cleared (e.g. `/#how-it-works` -> `/`).
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }
  }, [pathname, hash])

  return null
}
